import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CONTACT } from "@/lib/site";
import { Reveal } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  { value: "host", label: "Host a machine at my location" },
  { value: "partner", label: "Partnership / distribution" },
  { value: "general", label: "General enquiry" },
];

const field =
  "w-full bg-transparent border-0 border-b border-[#17320B]/25 rounded-none px-0 py-4 text-[#17320B] placeholder:text-[#17320B]/40 focus-visible:ring-0 focus-visible:border-[#17320B] text-lg";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", interest: "host", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Request received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", interest: "host", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="relative bg-[#C2E9FF]/40 py-24 md:py-40 border-t border-[#17320B]/10">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <Reveal>
            <span className="font-mono-eb text-xs uppercase tracking-[0.3em] text-[#7B9C62]">/ Get In Touch</span>
            <h2 className="font-display uppercase text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.9] mt-6 text-[#17320B]">
              Want a<br />
              <span className="text-[#7B9C62]">MacroBar</span><br />
              near you?
            </h2>
            <p className="mt-8 max-w-md text-[#17320B]/70 text-base md:text-lg">
              Gym, studio, office or campus {"\u2014"} tell us where and we&apos;ll bring fresh, functional
              nutrition to your space.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 space-y-4">
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="group flex items-center gap-4 text-[#17320B] hover:text-[#7B9C62] transition-colors duration-300">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#17320B]/20 group-hover:border-[#7B9C62]"><Mail size={18} /></span>
                <span className="font-display uppercase text-lg md:text-xl tracking-tight">{CONTACT.email}</span>
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} data-testid="contact-phone" className="group flex items-center gap-4 text-[#17320B] hover:text-[#7B9C62] transition-colors duration-300">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#17320B]/20 group-hover:border-[#7B9C62]"><Phone size={18} /></span>
                <span className="font-display uppercase text-lg md:text-xl tracking-tight">{CONTACT.phone}</span>
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form data-testid="contact-form" onSubmit={submit} className="space-y-7">
            <div className="grid sm:grid-cols-2 gap-7">
              <Input data-testid="input-name" className={field} placeholder="Full name *" value={form.name} onChange={set("name")} />
              <Input data-testid="input-email" type="email" className={field} placeholder="Email *" value={form.email} onChange={set("email")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-7">
              <Input data-testid="input-phone" className={field} placeholder="Phone" value={form.phone} onChange={set("phone")} />
              <Input data-testid="input-company" className={field} placeholder="Company / venue" value={form.company} onChange={set("company")} />
            </div>

            <div>
              <label className="font-mono-eb text-[10px] uppercase tracking-[0.2em] text-[#7B9C62]">I&apos;m interested in</label>
              <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                <SelectTrigger data-testid="select-interest" className="mt-3 w-full bg-transparent border-0 border-b border-[#17320B]/25 rounded-none px-0 py-4 text-[#17320B] text-lg focus:ring-0 focus:border-[#17320B]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white text-[#17320B] border-[#17320B]/15">
                  {INTERESTS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`interest-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Textarea data-testid="input-message" className={`${field} min-h-[110px] resize-none`} placeholder="Tell us about your location" value={form.message} onChange={set("message")} />

            <button
              type="submit"
              data-testid="submit-lead"
              disabled={loading}
              className="btn-sweep inline-flex items-center gap-2 rounded-full bg-[#17320B] px-9 py-4 font-mono-eb text-xs uppercase tracking-[0.15em] text-[#D9F4CD] font-semibold disabled:opacity-60"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Sending" : "Request a Machine"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
